import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type RowSelectionState,
  type SortingFn,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Info,
  Loader2,
  PackageSearch,
  Percent,
  Plus,
  RefreshCw,
  RotateCcw,
  Scale,
  Settings2,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DataTableRow, pinnedStyle } from '@/components/ui/data-table-row';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { LineItemStatusBadge } from './LineItemStatusBadge';
import { LineItemInfoPopover } from './LineItemInfoPopover';
import { EditableSpecCell } from './EditableSpecCell';
import { BulkEditDialog } from './BulkEditDialog';
import { BulkDeleteConfirm } from './BulkDeleteConfirm';
import { ExtractFromSourceDialog } from './ExtractFromSourceDialog';
import { IntakeSourceForm } from './IntakeSourceForm';
import { useQuotationDraftStore } from '../store/quotationDraftStore';
import {
  SpecAdvisoryBadges,
  SpecAdvisoryRowChip,
  computeSpecAdvisories,
  advisoryForLine,
  type SpecAdvisory,
  type SpecRule,
} from '@shared/lineItems';
import {
  QUICK_SELECT_KEYS,
  QUICK_SELECT_LABELS,
  useQuotationPreferencesStore,
} from '../store/quotationPreferencesStore';
import {
  DEFAULT_PRICE_LEVEL,
  EDITABLE_DIAMETERS_MM,
  EDITABLE_GRADES,
  EDITABLE_LENGTHS_M,
  PRICE_LEVEL_OPTIONS,
  PriceLevel,
  type QuotationDraft,
  type QuotationLineItemDto,
} from '../types/quotation.types';
import {
  colorsForDiameter,
  computePricing,
  computePricingContext,
  computeWeightBreakdown,
  CURRENT_SIZE_RULE,
  formatMarkupPct,
  formatPhp,
  formatSmallPct,
  formatWeight,
  isSmallChipDiameter,
  LEGACY_SIZE_RULE,
  type SizeRule,
} from '../utils/pricing';
import {
  postedRowPricing,
  postedWeightRows,
  selectPricingView,
  summarizePostedPricing,
} from '../utils/postedPricing';
import type { BaseMarkup } from '@features/quotation-intake/types/quotationIntake.types';
import {
  isEditableRow,
  isKgOnlyDraft,
  isPostedRow,
  isSpecValidForRematch,
  KG_ONLY_NOTE,
  revertPatch,
} from '../utils/lineItem';
import { sourceNote } from '../utils/cuttingListQuantity';

// 86d3dxnd7: gate on the server validator's rules (valid grade + positive
// diameter/length), not mere presence — else a bulk re-match fires a request
// that 400s on an invalid spec.
function specsComplete(item: QuotationLineItemDto): boolean {
  return isSpecValidForRematch(item);
}

interface QuotationLineItemsTableProps {
  draft: QuotationDraft;
  baseMarkup: BaseMarkup | null;
  /** Submitted quotations: lock all row editing, hide add/remove controls. */
  readOnly?: boolean;
}

const PRICE_LEVEL_CELL_OPTIONS = PRICE_LEVEL_OPTIONS.map((o) => ({
  value: o.value as string,
  label: o.label,
}));

type LineItemsViewMode = 'all' | 'spec' | 'pricing';

const DIAMETER_OPTIONS = EDITABLE_DIAMETERS_MM.map((d) => ({ value: String(d), label: `${d} mm` }));
const LENGTH_OPTIONS = EDITABLE_LENGTHS_M.map((l) => ({ value: String(l), label: `${l} m` }));
const GRADE_OPTIONS = EDITABLE_GRADES.map((g) => ({ value: g, label: `G${g}` }));
const WELDABLE_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

const PINNED_LEFT = ['_select', '_rowNum', 'matchedSku'];
const LAST_PINNED_ID = PINNED_LEFT[PINNED_LEFT.length - 1];

function MatchedSkuCell({ item }: { item: QuotationLineItemDto }) {
  const desc = item.matchedSalesDescription?.trim();
  const raw = item.rawDescription?.trim();

  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      {desc ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="block max-w-[260px] truncate align-middle text-sm text-foreground cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2">
                {desc}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-xs whitespace-pre-wrap">{desc}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      )}
      {raw && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="block max-w-[260px] truncate text-[11px] leading-tight text-muted-foreground cursor-help"
                title={raw}
              >
                {raw}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-xs whitespace-pre-wrap">
                <span className="font-medium">Source:</span> {raw}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

function StatusCell({ item }: { item: QuotationLineItemDto }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {item.matchStatus ? (
        <LineItemStatusBadge status={item.matchStatus} />
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      )}
      {item.isMatching && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      {!item.isMatching && item.matchError && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <AlertCircle
                className="h-3.5 w-3.5 text-destructive cursor-help"
                role="img"
                aria-label={`Re-match failed: ${item.matchError}`}
              />
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">{item.matchError}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {item.isEdited && (
        <Badge variant="outline" className="border-warning/30 bg-warning/10 px-1.5 py-0 text-[10px] font-medium text-warning">
          Edited
        </Badge>
      )}
    </div>
  );
}

function SortIcon({ dir }: { dir: false | 'asc' | 'desc' }) {
  if (dir === 'asc') return <ArrowUp className="ml-1 h-3.5 w-3.5" />;
  if (dir === 'desc') return <ArrowDown className="ml-1 h-3.5 w-3.5" />;
  return <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground/50" />;
}

function SortHeader({
  label,
  column,
  align = 'left',
}: {
  // ReactNode (not just string) so a header can embed a tooltip-wrapped label
  // (e.g. the compact "W" Weldable header) while keeping the sort toggle (86d3fb5yh).
  label: React.ReactNode;
  column: Column<QuotationLineItemDto, unknown>;
  align?: 'left' | 'right';
}) {
  const alignClass = align === 'right' ? 'justify-end -mr-3' : 'justify-start -ml-3';
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={`h-8 w-full px-2 font-medium ${alignClass}`}
      onClick={column.getToggleSortingHandler()}
    >
      {label}
      <SortIcon dir={column.getIsSorted()} />
    </Button>
  );
}


export function QuotationLineItemsTable({ draft, baseMarkup, readOnly = false }: QuotationLineItemsTableProps) {
  const lineItems = draft.lineItems;
  const visibleQuickSelects = useQuotationPreferencesStore((s) => s.visibleQuickSelects);
  const setQuickSelectVisible = useQuotationPreferencesStore((s) => s.setQuickSelectVisible);
  const [newlyAddedId, setNewlyAddedId] = useState<string | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [extractOpen, setExtractOpen] = useState(false);
  const isExtracting = draft.extractionStatus === 'processing';
  // 86d3cxrjc: default to All view — show spec + pricing columns up front so the
  // full line is visible without toggling. (Was 'spec' under CSC-291.)
  const [viewMode, setViewMode] = useState<LineItemsViewMode>('all');
  const showSpec = viewMode === 'all' || viewMode === 'spec';
  const showPricing = viewMode === 'all' || viewMode === 'pricing';

  const updateLineItem = useQuotationDraftStore((s) => s.updateLineItem);
  const addLineItem = useQuotationDraftStore((s) => s.addLineItem);
  const removeLineItem = useQuotationDraftStore((s) => s.removeLineItem);
  const rematchLineItem = useQuotationDraftStore((s) => s.rematchLineItem);
  const extractIntoDraft = useQuotationDraftStore((s) => s.extractIntoDraft);

  // Spec-rule advisories (86d3hk6pg): flag lines violating a sales spec rule
  // (≥16mm→G60, G60→weldable). Extraction ingests the client's actual request; the
  // coordinator applies fixes optionally via the toolbar badges.
  const specAdvisories = useMemo(
    () => computeSpecAdvisories(lineItems, (li) => li.id),
    [lineItems],
  );

  type QuotationLine = (typeof lineItems)[number];

  // Core: apply a spec fix to N lines + offer Undo. updateLineItem auto-recomputes isEdited
  // (diff vs originalSpec) and auto-schedules a re-match when spec fields change — so no
  // manual rematch/isEdited here, and no price-level handling (quotation has none). Stable
  // (deps: draft.id, updateLineItem) so the per-row chip doesn't churn the columns memo.
  const applySpecFix = useCallback(
    (affected: QuotationLine[], meta: Pick<SpecRule, 'label' | 'patch'>) => {
      if (affected.length === 0) return;
      const snapshot = affected.map((li) => ({
        id: li.id,
        gradeCode: li.gradeCode,
        isWeldable: li.isWeldable,
      }));
      for (const li of affected) updateLineItem(draft.id, li.id, meta.patch);
      toast.success(
        `${meta.label} — applied to ${affected.length} line${affected.length === 1 ? '' : 's'}`,
        {
          action: {
            label: 'Undo',
            onClick: () => {
              for (const s of snapshot) {
                updateLineItem(draft.id, s.id, {
                  gradeCode: s.gradeCode,
                  isWeldable: s.isWeldable,
                });
              }
            },
          },
        },
      );
    },
    [draft.id, updateLineItem],
  );

  // Aggregate toolbar badge → apply to all affected lines.
  const applyAdvisory = useCallback(
    (advisory: SpecAdvisory) => {
      const ids = new Set(advisory.ids);
      applySpecFix(lineItems.filter((li) => ids.has(li.id)), advisory);
    },
    [lineItems, applySpecFix],
  );

  // Per-row Status-cell chip → apply just this line's rule.
  const applyRowAdvisory = useCallback(
    (line: QuotationLine) => {
      const rule = advisoryForLine(line);
      if (rule) applySpecFix([line], rule);
    },
    [applySpecFix],
  );

  // Prune selection keys pointing to rows that no longer exist.
  useEffect(() => {
    const validIds = new Set(lineItems.map((li) => li.id));
    setRowSelection((prev) => {
      let changed = false;
      const next: RowSelectionState = {};
      for (const [id, v] of Object.entries(prev)) {
        if (validIds.has(id)) next[id] = v;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [lineItems]);

  const selectedCount = Object.values(rowSelection).filter(Boolean).length;

  useEffect(() => {
    if (selectedCount === 0) {
      setBulkEditOpen(false);
      setBulkDeleteOpen(false);
    }
  }, [selectedCount]);

  // 14ykpqtr22u — ONE pricing source for every price, band and weight figure in this table.
  // A posted quotation shows what was posted (its frozen snapshot), under the size rule it was
  // posted with; a posted quotation with no snapshot recomputes under the legacy rule; anything
  // else prices live under the current rule. Keyed on status, never on readOnly (a quotation is
  // also read-only while its post is in flight, before any snapshot exists). The rule is always
  // one of two module-level constants, so memo dependencies stay reference-stable (86d3d1z40).
  const pricingView = selectPricingView(draft.status, draft.submissionSnapshot);
  const postedSnapshot = pricingView === 'posted' ? (draft.submissionSnapshot ?? null) : null;
  const postedSummary = useMemo(
    () => (postedSnapshot ? summarizePostedPricing(postedSnapshot) : null),
    [postedSnapshot],
  );
  const sizeRule: SizeRule =
    postedSummary?.rule ?? (pricingView === 'unrecorded' ? LEGACY_SIZE_RULE : CURRENT_SIZE_RULE);

  const pricingContext = useMemo(
    () => (postedSnapshot ? null : computePricingContext(lineItems, sizeRule)),
    [postedSnapshot, lineItems, sizeRule],
  );
  const adjustment = pricingContext?.adjustment ?? 0;
  const taxRate = draft.taxRate;

  const priceRow = useCallback(
    (li: QuotationLineItemDto) =>
      postedSnapshot
        ? postedRowPricing(li, postedSnapshot, taxRate)
        : computePricing(li, baseMarkup, adjustment, taxRate),
    [postedSnapshot, baseMarkup, adjustment, taxRate],
  );

  // Totals count only rows that post (isPostedRow) — the same rows the server prices and bills.
  const totalGrossAmount = useMemo(() => {
    let total = 0;
    let hasAny = false;
    for (const li of lineItems) {
      if (!isPostedRow(li)) continue;
      const pricing = priceRow(li);
      if (pricing) {
        total += pricing.grossAmount;
        hasAny = true;
      }
    }
    return hasAny ? total : null;
  }, [lineItems, priceRow]);

  // 14ykpqtr22u — what the Tier Markup Adjustment band shows: the live context, or what was posted.
  // v1 snapshots (posted 27 Apr – 7 May 2026) recorded no tier or mix — recomputing the letters would
  // mislead (the Mix B cut-off moved on 28 Apr), so they show prices and the stored small % only.
  const tierBand = useMemo(() => {
    if (postedSummary) {
      return {
        asPosted: true,
        adjustment: postedSummary.adjustment,
        showLetters: !postedSummary.isV1,
        tier: postedSummary.tier,
        sizeMix: postedSummary.sizeMix,
        totalWeightKg: postedSummary.totalWeightKg,
        smallPct: postedSummary.smallPct,
        noPricesNote: postedSummary.hasAnyPrice ? null : 'No prices were sent with this posting.',
        note: postedSummary.isV1 ? "Tier and size mix weren't recorded before 8 May 2026." : null,
      };
    }
    if (!pricingContext) return null;
    return {
      asPosted: false,
      adjustment: pricingContext.adjustment as number | null,
      showLetters: true,
      tier: pricingContext.tier as string | null,
      sizeMix: pricingContext.sizeMix as string | null,
      totalWeightKg: pricingContext.totalWeightKg as number | null,
      smallPct: pricingContext.smallPct as number | null,
      noPricesNote: null,
      note:
        pricingView === 'unrecorded'
          ? "Pricing wasn't recorded when this was posted; figures use today's base markup."
          : null,
    };
  }, [postedSummary, pricingContext, pricingView]);

  const revertOne = (item: QuotationLineItemDto): boolean => {
    if (!item.originalSpec) return false;
    // QUO-R-004 — the snapshot holds the normalized value, so Revert lands on whole pieces.
    updateLineItem(draft.id, item.id, revertPatch(item.originalSpec));
    return true;
  };

  const handleRevert = (itemId: string) => {
    const item = lineItems.find((li) => li.id === itemId);
    if (item) revertOne(item);
  };

  const handleAddLine = () => {
    const id = addLineItem(draft.id);
    setNewlyAddedId(id);
    setTimeout(() => setNewlyAddedId(null), 0);
  };

  const pricingSortFn = (
    key: 'rate' | 'rateWithMarkup' | 'amount' | 'grossAmount',
  ): SortingFn<QuotationLineItemDto> => (rowA, rowB) => {
    const a = priceRow(rowA.original)?.[key] ?? null;
    const b = priceRow(rowB.original)?.[key] ?? null;
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a - b;
  };

  // CSC-293: single-pass derivation of every quick-select id list and the four
  // per-column value→ids indexes used by the right-click context menu. Lives
  // before the columns useMemo so cell renders can read it from the closure.
  // All ids are pre-filtered through isEditableRow so non-editable rows
  // (SUMMARY_ONLY / EXTERNAL_ATTACHMENT) can never leak into rowSelection.
  const lineItemDerivations = useMemo(() => {
    const small: string[] = [];
    const big: string[] = [];
    const noLength: string[] = [];
    const noGrade: string[] = [];
    const noQuantity: string[] = [];
    const noMatch: string[] = [];
    const weldable: string[] = [];
    const notWeldable: string[] = [];
    const byDiameter = new Map<number | null, string[]>();
    const byLength = new Map<number | null, string[]>();
    const byGrade = new Map<string | null, string[]>();
    const byWeldable = new Map<boolean, string[]>();
    const push = <K,>(m: Map<K, string[]>, k: K, id: string) => {
      const arr = m.get(k);
      if (arr) arr.push(id);
      else m.set(k, [id]);
    };
    for (const li of lineItems) {
      if (!isEditableRow(li)) continue;
      // 14ykpqtr22u — split at BIG_DIAMETER_MIN_MM (16mm is big): every sized row lands in exactly
      // one chip (the old ≤16 / ≥20 cut-offs left 18mm in neither).
      if (li.diameterMm != null) (isSmallChipDiameter(li.diameterMm) ? small : big).push(li.id);
      if (li.lengthM == null) noLength.push(li.id);
      if (li.gradeCode == null) noGrade.push(li.id);
      if (li.quantity == null || li.quantity === 0) noQuantity.push(li.id);
      if (li.matchStatus === 'NO_MATCH') noMatch.push(li.id);
      if (li.isWeldable === true) weldable.push(li.id);
      if (li.isWeldable === false) notWeldable.push(li.id);
      push(byDiameter, li.diameterMm ?? null, li.id);
      push(byLength, li.lengthM ?? null, li.id);
      push(byGrade, li.gradeCode ?? null, li.id);
      push(byWeldable, li.isWeldable, li.id);
    }
    return { small, big, noLength, noGrade, noQuantity, noMatch, weldable, notWeldable, byDiameter, byLength, byGrade, byWeldable };
  }, [lineItems]);

  // useCallback with [] deps — the only non-stable reference (`setRowSelection`
  // from useState) is itself stable across renders, so the callbacks are safe
  // to capture inside the columns useMemo without invalidating it.
  const setOnlyGroup = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    const next: RowSelectionState = {};
    for (const id of ids) next[id] = true;
    setRowSelection(next);
  }, []);
  const addToSelection = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setRowSelection((prev) => {
      const next = { ...prev };
      for (const id of ids) next[id] = true;
      return next;
    });
  }, []);
  const removeFromSelection = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setRowSelection((prev) => {
      const next = { ...prev };
      for (const id of ids) delete next[id];
      return next;
    });
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<ColumnDef<QuotationLineItemDto, any>[]>(() => {
    const helper = createColumnHelper<QuotationLineItemDto>();
    return [
      helper.display({
        id: '_select',
        size: 36,
        enableSorting: false,
        header: ({ table }) => {
          const selectable = table.getRowModel().rows.filter((r) => r.getCanSelect());
          const allSelected = selectable.length > 0 && selectable.every((r) => r.getIsSelected());
          const someSelected = selectable.some((r) => r.getIsSelected());
          const state: boolean | 'indeterminate' = allSelected
            ? true
            : someSelected
              ? 'indeterminate'
              : false;
          return (
            <Checkbox
              checked={state}
              onCheckedChange={() => {
                if (state === true) {
                  setRowSelection({});
                } else {
                  const next: RowSelectionState = {};
                  selectable.forEach((r) => (next[r.id] = true));
                  setRowSelection(next);
                }
              }}
              disabled={selectable.length === 0}
              aria-label="Select all editable rows"
            />
          );
        },
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(!!v)}
            disabled={!row.getCanSelect()}
            aria-label={`Select row ${row.index + 1}`}
          />
        ),
        meta: { headerClassName: 'pr-0', cellClassName: 'pr-0' },
      }),
      helper.display({
        id: '_rowNum',
        size: 48,
        enableSorting: false,
        header: () => <span>#</span>,
        cell: ({ row }) => <span className="text-muted-foreground">{row.index + 1}</span>,
      }),
      helper.accessor(
        (row) => row.matchedSalesDescription ?? row.rawDescription ?? null,
        {
          id: 'matchedSku',
          size: 220,
          sortingFn: 'alphanumeric',
          sortUndefined: 'last',
          header: ({ column }) => <SortHeader label="Matched SKU" column={column} />,
          cell: ({ row }) => <MatchedSkuCell item={row.original} />,
        },
      ),
      helper.accessor('diameterMm', {
        id: 'diameter',
        size: 110,
        sortingFn: 'basic',
        sortUndefined: 'last',
        header: ({ column }) => <SortHeader label="Diameter" column={column} />,
        cell: ({ row }) => {
          const li = row.original;
          return (
            <SpecContextMenu
              readOnly={readOnly}
              matchIds={lineItemDerivations.byDiameter.get(li.diameterMm ?? null) ?? []}
              label={li.diameterMm != null ? `${li.diameterMm}mm` : '— (no diameter)'}
              onSelectOnly={setOnlyGroup}
              onAdd={addToSelection}
              onRemove={removeFromSelection}
            >
              <EditableSpecCell
                kind="select"
                value={li.diameterMm != null ? String(li.diameterMm) : null}
                options={DIAMETER_OPTIONS}
                onCommit={(v) =>
                  updateLineItem(draft.id, li.id, { diameterMm: v == null ? null : Number(v) })
                }
                disabled={!isEditableRow(li)}
                autoFocus={li.id === newlyAddedId}
                ariaLabel="Diameter"
              />
            </SpecContextMenu>
          );
        },
        meta: { cellClassName: 'p-1' },
      }),
      helper.accessor('lengthM', {
        id: 'length',
        size: 110,
        sortingFn: 'basic',
        sortUndefined: 'last',
        header: ({ column }) => <SortHeader label="Length" column={column} />,
        cell: ({ row }) => {
          const li = row.original;
          return (
            <SpecContextMenu
              readOnly={readOnly}
              matchIds={lineItemDerivations.byLength.get(li.lengthM ?? null) ?? []}
              label={li.lengthM != null ? `${li.lengthM}m` : '— (no length)'}
              onSelectOnly={setOnlyGroup}
              onAdd={addToSelection}
              onRemove={removeFromSelection}
            >
              <EditableSpecCell
                kind="select"
                value={li.lengthM != null ? String(li.lengthM) : null}
                options={LENGTH_OPTIONS}
                onCommit={(v) =>
                  updateLineItem(draft.id, li.id, { lengthM: v == null ? null : Number(v) })
                }
                disabled={!isEditableRow(li)}
                ariaLabel="Length"
              />
            </SpecContextMenu>
          );
        },
        meta: { cellClassName: 'p-1' },
      }),
      helper.accessor('gradeCode', {
        id: 'grade',
        size: 90,
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        header: ({ column }) => <SortHeader label="Grade" column={column} />,
        cell: ({ row }) => {
          const li = row.original;
          return (
            <SpecContextMenu
              readOnly={readOnly}
              matchIds={lineItemDerivations.byGrade.get(li.gradeCode ?? null) ?? []}
              label={li.gradeCode != null ? `G${li.gradeCode}` : '— (no grade)'}
              onSelectOnly={setOnlyGroup}
              onAdd={addToSelection}
              onRemove={removeFromSelection}
            >
              <div className="flex items-center gap-1">
                <EditableSpecCell
                  kind="select"
                  value={li.gradeCode}
                  options={GRADE_OPTIONS}
                  onCommit={(v) => updateLineItem(draft.id, li.id, { gradeCode: v })}
                  disabled={!isEditableRow(li)}
                  ariaLabel="Grade"
                />
                {/* CSC-291: badge marks lines whose grade was auto-promoted at extraction. */}
                {li.promotedFromGrade != null && (
                  <span
                    className="rounded bg-warning/15 px-1 text-[10px] font-semibold text-warning"
                    title={`Promoted from G${li.promotedFromGrade} per CSC grade policy.`}
                  >
                    G{li.promotedFromGrade}↑
                  </span>
                )}
              </div>
            </SpecContextMenu>
          );
        },
        meta: { cellClassName: 'p-1' },
      }),
      helper.accessor('isWeldable', {
        id: 'weldable',
        size: 80,
        sortingFn: (a, b) => Number(a.original.isWeldable) - Number(b.original.isWeldable),
        // Compact "W" header with a hover hint — the full word doesn't fit the dense
        // column and the Y/N cell is self-explanatory once known (86d3fb5yh, mirrors SO
        // 86d3f8234). Kept sortable: the tooltip trigger is a `<span>` via `asChild`, so
        // there's no nested button inside SortHeader's sort button. Carries its own
        // TooltipProvider (Quotation has no global one).
        header: ({ column }) => (
          <SortHeader
            label={
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-default underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
                      W
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs font-normal">Weldable</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            }
            column={column}
          />
        ),
        cell: ({ row }) => {
          const li = row.original;
          return (
            <SpecContextMenu
              readOnly={readOnly}
              matchIds={lineItemDerivations.byWeldable.get(li.isWeldable) ?? []}
              label={li.isWeldable ? 'Weldable' : 'Non-weldable'}
              onSelectOnly={setOnlyGroup}
              onAdd={addToSelection}
              onRemove={removeFromSelection}
            >
              <EditableSpecCell
                kind="select"
                value={li.isWeldable ? 'Y' : 'N'}
                options={WELDABLE_OPTIONS}
                onCommit={(v) => updateLineItem(draft.id, li.id, { isWeldable: v === 'Y' })}
                disabled={!isEditableRow(li)}
                ariaLabel="Weldable"
                placeholder="N"
              />
            </SpecContextMenu>
          );
        },
        meta: { cellClassName: 'p-1' },
      }),
      helper.accessor('quantity', {
        id: 'qty',
        size: 100,
        sortingFn: 'basic',
        sortUndefined: 'last',
        header: ({ column }) => <SortHeader label="Qty" column={column} align="right" />,
        cell: ({ row }) => {
          const li = row.original;
          // Requirement 6 (14ykpqtw3hw) — the customer's own value as a muted second line ("asked 100.2" /
          // "asked 51,366.07 kg"), the full sentence in a NATIVE title: this repeats per row, so never a Radix
          // Tooltip here (UI-GUIDELINES §8, the 239 ms lesson).
          const note = sourceNote(li);
          return (
            <div>
              <EditableSpecCell
                kind="number"
                value={li.quantity}
                onCommit={(v) => updateLineItem(draft.id, li.id, { quantity: v })}
                min={1}
                step={1}
                disabled={!isEditableRow(li)}
                ariaLabel="Quantity"
              />
              {note && (
                <div
                  className="cursor-help truncate px-1.5 text-right text-xs leading-tight text-muted-foreground"
                  title={note.title}
                >
                  {note.label}
                </div>
              )}
            </div>
          );
        },
        // min-w floor: the cell is a `w-full` <input> with tiny min-content, so under
        // table-layout:auto the browser squeezes this column to feed the long SKU column
        // (the inline `size` is only a suggestion, no floor) and the value clips (86d3fb5yh).
        meta: { cellClassName: 'p-1 min-w-[100px]', headerClassName: 'text-right' },
      }),
      helper.accessor('unit', {
        id: 'unit',
        size: 80,
        // Read-only (86d3dxnd7). Pieces on every posted row; 14ykpqtw3hw: a kg order with no exact catalogue
        // item stays in kg, unmatched (QUO-R-003e), so the cell says which.
        header: 'Unit',
        cell: ({ row }) => <div className="px-1.5 py-1 text-sm text-foreground">{row.original.unit}</div>,
      }),
      helper.accessor('weightKg', {
        id: 'weight',
        size: 110,
        sortingFn: 'basic',
        sortUndefined: 'last',
        header: ({ column }) => <SortHeader label="Weight" column={column} align="right" />,
        cell: ({ row }) =>
          row.original.weightKg != null ? (
            <span className="tabular-nums">
              {row.original.weightKg.toLocaleString()}
              <span className="ml-1 text-muted-foreground">kg</span>
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        meta: { cellClassName: 'text-right', headerClassName: 'text-right' },
      }),
      helper.accessor('priceLevel', {
        id: 'priceLevel',
        size: 130,
        sortingFn: 'alphanumeric',
        header: ({ column }) => <SortHeader label="Price Level" column={column} />,
        cell: ({ row }) => {
          const li = row.original;
          return (
            <EditableSpecCell
              kind="select"
              value={(li.priceLevel ?? DEFAULT_PRICE_LEVEL) as string}
              options={PRICE_LEVEL_CELL_OPTIONS}
              onCommit={(v) =>
                updateLineItem(draft.id, li.id, {
                  priceLevel: (v ?? DEFAULT_PRICE_LEVEL) as PriceLevel,
                })
              }
              disabled={!isEditableRow(li)}
              ariaLabel="Price Level"
              placeholder="Base Price"
            />
          );
        },
        meta: { cellClassName: 'p-1' },
      }),
      helper.display({
        id: 'rate',
        size: 120,
        enableSorting: true,
        sortingFn: pricingSortFn('rate'),
        header: ({ column }) => <SortHeader label="Rate" column={column} align="right" />,
        cell: ({ row }) => {
          const pricing = priceRow(row.original);
          return <span className="tabular-nums">{formatPhp(pricing?.rate ?? null)}</span>;
        },
        meta: { cellClassName: 'text-right', headerClassName: 'text-right' },
      }),
      helper.display({
        id: 'rateWithMarkup',
        size: 170,
        enableSorting: true,
        sortingFn: pricingSortFn('rateWithMarkup'),
        header: ({ column }) => <SortHeader label="Rate w/ Markup" column={column} align="right" />,
        cell: ({ row }) => {
          const pricing = priceRow(row.original);
          if (!pricing) return <span>—</span>;
          return (
            <span className="tabular-nums">
              {formatPhp(pricing.rateWithMarkup)}
              <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                {formatMarkupPct(pricing.markupPct)}
              </span>
            </span>
          );
        },
        meta: { cellClassName: 'text-right', headerClassName: 'text-right' },
      }),
      helper.display({
        id: 'amount',
        size: 130,
        enableSorting: true,
        sortingFn: pricingSortFn('amount'),
        header: ({ column }) => <SortHeader label="Amount" column={column} align="right" />,
        cell: ({ row }) => {
          const pricing = priceRow(row.original);
          return <span className="tabular-nums">{formatPhp(pricing?.amount ?? null)}</span>;
        },
        meta: { cellClassName: 'text-right', headerClassName: 'text-right' },
      }),
      helper.display({
        id: 'grossAmount',
        size: 140,
        enableSorting: true,
        sortingFn: pricingSortFn('grossAmount'),
        header: ({ column }) => <SortHeader label="Gross Amount" column={column} align="right" />,
        cell: ({ row }) => {
          const pricing = priceRow(row.original);
          return <span className="tabular-nums">{formatPhp(pricing?.grossAmount ?? null)}</span>;
        },
        meta: { cellClassName: 'text-right', headerClassName: 'text-right' },
      }),
      helper.accessor((row) => row.matchStatus ?? null, {
        id: 'status',
        size: 180,
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        header: ({ column }) => <SortHeader label="Status" column={column} />,
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <StatusCell item={row.original} />
            {!readOnly && (
              <SpecAdvisoryRowChip
                line={row.original}
                onApply={() => applyRowAdvisory(row.original)}
              />
            )}
          </div>
        ),
      }),
      helper.display({
        id: '_actions',
        size: 48,
        enableSorting: false,
        header: () => null,
        cell: ({ row }) => (
          <LineItemInfoPopover
            item={row.original}
            onRevert={row.original.originalSpec ? () => handleRevert(row.original.id) : undefined}
            onDelete={() => removeLineItem(draft.id, row.original.id)}
          />
        ),
        meta: { cellClassName: 'text-right' },
      }),
    ];
  }, [
    draft.id,
    // 14ykpqtr22u — every price cell and sort reads priceRow (posted snapshot or live pricing);
    // it changes whenever the markup, adjustment, tax rate or posted snapshot does.
    priceRow,
    newlyAddedId,
    updateLineItem,
    removeLineItem,
    readOnly,
    lineItemDerivations,
    setOnlyGroup,
    addToSelection,
    removeFromSelection,
    applyRowAdvisory,
  ]);

  const columnVisibility: VisibilityState = useMemo(
    () => ({
      // Submitted quotations: drop the row checkboxes (no edit → nothing to bulk-action on)
      // and the per-row actions column (revert/delete also only apply to editable drafts).
      _select: !readOnly,
      _actions: !readOnly,
      diameter: showSpec,
      length: showSpec,
      grade: showSpec,
      weldable: showSpec,
      priceLevel: showPricing,
      rate: showPricing,
      rateWithMarkup: showPricing,
      amount: showPricing,
      grossAmount: showPricing,
    }),
    [showSpec, showPricing, readOnly],
  );

  const table = useReactTable<QuotationLineItemDto>({
    data: lineItems,
    columns,
    state: { sorting, rowSelection, columnVisibility },
    initialState: { columnPinning: { left: PINNED_LEFT } },
    getRowId: (row) => row.id,
    enableRowSelection: (row) => isEditableRow(row.original),
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const toggleGroupSelection = (ids: string[]) => {
    if (ids.length === 0) return;
    setRowSelection((prev) => {
      const allIn = ids.every((id) => prev[id]);
      const next = { ...prev };
      if (allIn) ids.forEach((id) => delete next[id]);
      else ids.forEach((id) => (next[id] = true));
      return next;
    });
  };

  const selectedItems = (): QuotationLineItemDto[] =>
    table.getSelectedRowModel().rows.map((r) => r.original);

  const clearSelection = () => setRowSelection({});

  const onBulkApply = (patch: Partial<QuotationLineItemDto>) => {
    const items = selectedItems();
    items.forEach((li) => updateLineItem(draft.id, li.id, patch));
    toast.success(`Applied to ${items.length} row${items.length === 1 ? '' : 's'}.`);
  };

  const onBulkDelete = () => {
    const items = selectedItems();
    items.forEach((li) => removeLineItem(draft.id, li.id));
    clearSelection();
  };

  const onBulkRevert = () => {
    let reverted = 0;
    let skipped = 0;
    for (const item of selectedItems()) {
      if (revertOne(item)) reverted++;
      else skipped++;
    }
    if (reverted > 0) toast.success(`Reverted ${reverted} row${reverted === 1 ? '' : 's'}.`);
    if (skipped > 0)
      toast.info(`${skipped} user-added row${skipped === 1 ? '' : 's'} skipped (no extracted original).`);
  };

  const onBulkRematch = () => {
    let triggered = 0;
    let skipped = 0;
    for (const item of selectedItems()) {
      if (specsComplete(item)) {
        triggered++;
        void rematchLineItem(draft.id, item.id);
      } else {
        skipped++;
      }
    }
    if (triggered > 0) toast.info(`Re-matching ${triggered} row${triggered === 1 ? '' : 's'}…`);
    if (skipped > 0)
      toast.info(`${skipped} row${skipped === 1 ? '' : 's'} skipped (incomplete specs).`);
  };

  // CSC-293: id arrays come from the single-pass `lineItemDerivations` above.
  // Aliased here so the toolbar JSX reads naturally.
  const smallDiameterIds = lineItemDerivations.small;
  const bigDiameterIds = lineItemDerivations.big;
  const noLengthIds = lineItemDerivations.noLength;
  const noGradeIds = lineItemDerivations.noGrade;
  const noQuantityIds = lineItemDerivations.noQuantity;
  const noMatchIds = lineItemDerivations.noMatch;
  const weldableIds = lineItemDerivations.weldable;
  const notWeldableIds = lineItemDerivations.notWeldable;

  const allSmallSelected =
    smallDiameterIds.length > 0 && smallDiameterIds.every((id) => rowSelection[id]);
  const allBigSelected =
    bigDiameterIds.length > 0 && bigDiameterIds.every((id) => rowSelection[id]);
  const allNoLengthSelected =
    noLengthIds.length > 0 && noLengthIds.every((id) => rowSelection[id]);
  const allNoGradeSelected =
    noGradeIds.length > 0 && noGradeIds.every((id) => rowSelection[id]);
  const allNoQuantitySelected =
    noQuantityIds.length > 0 && noQuantityIds.every((id) => rowSelection[id]);
  const allNoMatchSelected =
    noMatchIds.length > 0 && noMatchIds.every((id) => rowSelection[id]);
  const allWeldableSelected =
    weldableIds.length > 0 && weldableIds.every((id) => rowSelection[id]);
  const allNotWeldableSelected =
    notWeldableIds.length > 0 && notWeldableIds.every((id) => rowSelection[id]);

  const summary = useMemo(() => {
    const s = { matched: 0, needsInfo: 0, noMatch: 0, other: 0 };
    for (const li of lineItems) {
      switch (li.matchStatus) {
        case 'MATCHED': s.matched++; break;
        case 'NEEDS_CLARIFICATION': s.needsInfo++; break;
        case 'NO_MATCH': s.noMatch++; break;
        case undefined: break;
        default: s.other++; break;
      }
    }
    return s;
  }, [lineItems]);

  // 14ykpqtr22u — a posted quotation's split comes from its posted lines under the rule it was
  // posted with; otherwise from the rows that post (kg / summary rows never count).
  const weightBreakdown = useMemo(
    () =>
      computeWeightBreakdown(
        postedSnapshot ? postedWeightRows(postedSnapshot) : lineItems.filter(isPostedRow),
        sizeRule,
      ),
    [postedSnapshot, lineItems, sizeRule],
  );
  const totalWeight = weightBreakdown.total;
  const hasMatchData = lineItems.some((li) => li.matchStatus);

  return (
    // CSC-291: drop the elevated Card chrome (rounded corners + shadow + side
    // borders) so the section reads as a flat band that hugs the panel edges,
    // matching the workspace's header style. Border-y kept as a separator.
    <Card className="rounded-none border-x-0 shadow-none">
      {/* CSC-291: tighten CardHeader padding (shadcn defaults to p-6 = 24px)
          to px-4 py-3 — matches the Tier Markup Adjustment band below for a
          uniform compact stack. */}
      {/* CSC-291: title + match-summary badges + total weight all sit on a
          single row alongside the view-mode tabs. `flex-wrap` lets the badges
          drop to a second line at narrow widths instead of clipping. */}
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            <PackageSearch className="h-4 w-4 text-muted-foreground" />
            Line Items
            {lineItems.length > 0 && (
              <span className="text-xs font-normal text-muted-foreground">
                · {lineItems.length} item{lineItems.length === 1 ? '' : 's'}
              </span>
            )}
          </CardTitle>
          {hasMatchData && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {summary.matched > 0 && (
                <Badge variant="success" className="text-xs">{summary.matched} matched</Badge>
              )}
              {summary.needsInfo > 0 && (
                <Badge variant="warning" className="text-xs">{summary.needsInfo} need info</Badge>
              )}
              {summary.noMatch > 0 && (
                <Badge variant="destructive" className="text-xs">{summary.noMatch} no match</Badge>
              )}
              {summary.other > 0 && (
                <Badge variant="outline" className="text-xs">{summary.other} other</Badge>
              )}
              <span className="text-muted-foreground tabular-nums">
                · Total weight {totalWeight.toLocaleString()} kg
              </span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* CSC-291: mirror Add Line + Re-extract here so they're reachable
              from the header without scrolling past the table. The action row
              above the Weight Breakdown still renders these for the
              after-table use case. */}
          {!readOnly && (
            <SpecAdvisoryBadges advisories={specAdvisories} onApply={applyAdvisory} />
          )}
          {!readOnly && (
            <>
              <Button type="button" variant="outline" size="sm" onClick={handleAddLine}>
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add Line
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setExtractOpen(true)}
                disabled={isExtracting}
                className={isExtracting ? 'border-info/30 bg-info/10 text-info disabled:opacity-100' : undefined}
                title={lineItems.length === 0 ? 'Paste text or upload a file to extract line items' : 'Replace the current line items with a fresh extraction'}
              >
                {isExtracting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin text-info" />
                    Extracting…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                    {lineItems.length === 0 ? 'Extract' : 'Re-extract'}
                  </>
                )}
              </Button>
            </>
          )}
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as LineItemsViewMode)}>
            <TabsList className="h-8">
              <TabsTrigger value="all" className="px-3 text-xs">All</TabsTrigger>
              <TabsTrigger value="spec" className="px-3 text-xs">Spec</TabsTrigger>
              <TabsTrigger value="pricing" className="px-3 text-xs">Pricing</TabsTrigger>
            </TabsList>
          </Tabs>
          {/* 86d3fb5yh: the standalone "View Source" button was removed (parity with
              SO 86d3f95ca). It duplicated the detail panel's top "Review" button, which
              opens QuotationReviewView (source vs extracted lines) on every surface. */}
        </div>
      </CardHeader>
      {tierBand && (
        <div className="border-t border-b border-l-4 border-l-info bg-info/10 px-4 py-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-info text-info-foreground shadow-sm">
                <Percent className="h-4 w-4" />
              </div>
              <div className="flex flex-col leading-tight">
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-info">
                    Tier Markup Adjustment
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="About the tier markup adjustment"
                          className="inline-flex rounded-sm text-info/70 hover:text-info focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-xs">
                          Added on top of each line's base per-grade markup. Determined by
                          total cutting-list weight (tier) and the share of small-diameter (
                          {sizeRule.smallDiametersMm.join('/')} mm) bars by weight (size mix).
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {tierBand.asPosted && (
                    <Badge
                      variant="outline"
                      className="border-info/30 bg-card px-1.5 py-0 text-xs font-medium text-info"
                    >
                      As posted
                    </Badge>
                  )}
                </div>
                <span className="text-[11px] text-info/70">
                  Applied to every line's price
                </span>
              </div>
            </div>

            {tierBand.adjustment != null && (
              <span className="text-2xl font-bold tabular-nums text-info">
                {formatMarkupPct(tierBand.adjustment)}
              </span>
            )}

            {tierBand.showLetters && (
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant="outline" className="border-info/30 bg-card px-2 py-0.5 text-xs font-semibold text-info">
                  Tier {tierBand.tier ?? '—'}
                </Badge>
                <Badge variant="outline" className="border-info/30 bg-card px-2 py-0.5 text-xs font-semibold text-info">
                  Mix {tierBand.sizeMix ?? '—'}
                </Badge>
              </div>
            )}

            <div className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-info/75">
              {tierBand.totalWeightKg != null && (
                <span className="tabular-nums">
                  <span className="font-semibold text-info">
                    {formatWeight(tierBand.totalWeightKg)}
                  </span>{' '}
                  total
                </span>
              )}
              {tierBand.totalWeightKg != null && tierBand.smallPct != null && (
                <span className="text-info/40">·</span>
              )}
              {tierBand.smallPct != null && (
                <span className="tabular-nums">
                  <span className="font-semibold text-info">
                    {formatSmallPct(tierBand.smallPct)}
                  </span>{' '}
                  small-diameter
                </span>
              )}
              {tierBand.noPricesNote ? (
                <>
                  <span className="hidden h-6 w-px bg-info/30 sm:inline-block" />
                  <span className="font-medium text-info">{tierBand.noPricesNote}</span>
                </>
              ) : (
                totalGrossAmount != null && (
                  <>
                    <span className="hidden h-6 w-px bg-info/30 sm:inline-block" />
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-info/80">
                        Total Gross
                      </span>
                      <span className="text-lg font-bold tabular-nums text-info">
                        {formatPhp(totalGrossAmount)}
                      </span>
                    </div>
                  </>
                )
              )}
            </div>
          </div>
          {tierBand.note && <p className="mt-2 text-xs text-muted-foreground">{tierBand.note}</p>}
        </div>
      )}
      {/* CSC-293: keep the Quick Select + Bulk Action toolbars pinned to the
          top of the scroll area while the table scrolls beneath. `bg-background`
          on the wrapper is what makes the (semi-transparent) toolbar bgs sit
          over a solid backdrop instead of letting the scrolled table bleed
          through. z-30 stays above the table's left-pinned columns (z-20). */}
      <div className="sticky top-0 z-30 bg-background shadow-[0_1px_0_0_hsl(var(--border))]">
      {lineItems.length > 0 && !readOnly && (
        <div className="flex flex-wrap items-center gap-2 border-t bg-muted/20 px-4 py-2 text-xs">
          <span className="font-medium text-muted-foreground">Quick select:</span>
          {visibleQuickSelects.smallDiameter && (
            <Button
              type="button"
              size="sm"
              variant={allSmallSelected ? 'default' : 'outline'}
              disabled={smallDiameterIds.length === 0}
              onClick={() => toggleGroupSelection(smallDiameterIds)}
              className={`h-7 px-2 text-xs ${allSmallSelected ? 'bg-info text-info-foreground hover:bg-info/90' : ''}`}
              title={smallDiameterIds.length === 0 ? 'No small-diameter rows' : 'Toggle selection of all small-diameter rows'}
            >
              {QUICK_SELECT_LABELS.smallDiameter} · {smallDiameterIds.length}
            </Button>
          )}
          {visibleQuickSelects.bigDiameter && (
            <Button
              type="button"
              size="sm"
              variant={allBigSelected ? 'default' : 'outline'}
              disabled={bigDiameterIds.length === 0}
              onClick={() => toggleGroupSelection(bigDiameterIds)}
              className={`h-7 px-2 text-xs ${allBigSelected ? 'bg-info text-info-foreground hover:bg-info/90' : ''}`}
              title={bigDiameterIds.length === 0 ? 'No big-diameter rows' : 'Toggle selection of all big-diameter rows'}
            >
              {QUICK_SELECT_LABELS.bigDiameter} · {bigDiameterIds.length}
            </Button>
          )}
          {visibleQuickSelects.noLength && (
            <Button
              type="button"
              size="sm"
              variant={allNoLengthSelected ? 'default' : 'outline'}
              disabled={noLengthIds.length === 0}
              onClick={() => toggleGroupSelection(noLengthIds)}
              className={`h-7 px-2 text-xs ${allNoLengthSelected ? 'bg-info text-info-foreground hover:bg-info/90' : ''}`}
              title={noLengthIds.length === 0 ? 'No rows missing a length' : 'Toggle selection of rows missing a length'}
            >
              No length · {noLengthIds.length}
            </Button>
          )}
          {visibleQuickSelects.noGrade && (
            <Button
              type="button"
              size="sm"
              variant={allNoGradeSelected ? 'default' : 'outline'}
              disabled={noGradeIds.length === 0}
              onClick={() => toggleGroupSelection(noGradeIds)}
              className={`h-7 px-2 text-xs ${allNoGradeSelected ? 'bg-info text-info-foreground hover:bg-info/90' : ''}`}
              title={noGradeIds.length === 0 ? 'No rows missing a grade' : 'Toggle selection of rows missing a grade'}
            >
              No grade · {noGradeIds.length}
            </Button>
          )}
          {visibleQuickSelects.noQuantity && (
            <Button
              type="button"
              size="sm"
              variant={allNoQuantitySelected ? 'default' : 'outline'}
              disabled={noQuantityIds.length === 0}
              onClick={() => toggleGroupSelection(noQuantityIds)}
              className={`h-7 px-2 text-xs ${allNoQuantitySelected ? 'bg-info text-info-foreground hover:bg-info/90' : ''}`}
              title={noQuantityIds.length === 0 ? 'No rows missing a quantity' : 'Toggle selection of rows missing a quantity'}
            >
              No qty · {noQuantityIds.length}
            </Button>
          )}
          {visibleQuickSelects.noMatch && (
            <Button
              type="button"
              size="sm"
              variant={allNoMatchSelected ? 'default' : 'outline'}
              disabled={noMatchIds.length === 0}
              onClick={() => toggleGroupSelection(noMatchIds)}
              className={`h-7 px-2 text-xs ${allNoMatchSelected ? 'bg-info text-info-foreground hover:bg-info/90' : ''}`}
              title={noMatchIds.length === 0 ? 'No NO_MATCH rows' : 'Toggle selection of NO_MATCH rows (excludes rows still needing clarification)'}
            >
              No match · {noMatchIds.length}
            </Button>
          )}
          {visibleQuickSelects.weldable && (
            <Button
              type="button"
              size="sm"
              variant={allWeldableSelected ? 'default' : 'outline'}
              disabled={weldableIds.length === 0}
              onClick={() => toggleGroupSelection(weldableIds)}
              className={`h-7 px-2 text-xs ${allWeldableSelected ? 'bg-info text-info-foreground hover:bg-info/90' : ''}`}
              title={weldableIds.length === 0 ? 'No weldable rows' : 'Toggle selection of weldable rows'}
            >
              Weldable · {weldableIds.length}
            </Button>
          )}
          {visibleQuickSelects.notWeldable && (
            <Button
              type="button"
              size="sm"
              variant={allNotWeldableSelected ? 'default' : 'outline'}
              disabled={notWeldableIds.length === 0}
              onClick={() => toggleGroupSelection(notWeldableIds)}
              className={`h-7 px-2 text-xs ${allNotWeldableSelected ? 'bg-info text-info-foreground hover:bg-info/90' : ''}`}
              title={notWeldableIds.length === 0 ? 'No non-weldable rows' : 'Toggle selection of non-weldable rows'}
            >
              Not weldable · {notWeldableIds.length}
            </Button>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="ml-auto h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                aria-label="Configure visible quick selects"
                title="Configure visible quick selects"
              >
                <Settings2 className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-60" align="end">
              <div className="space-y-3">
                <div>
                  <div className="text-sm font-semibold">Visible quick selects</div>
                  <p className="text-[11px] text-muted-foreground">
                    Choice persists in this browser.
                  </p>
                </div>
                <div className="space-y-2">
                  {QUICK_SELECT_KEYS.map((key) => (
                    <label
                      key={key}
                      className="flex cursor-pointer items-center gap-2 text-sm"
                    >
                      <Checkbox
                        checked={visibleQuickSelects[key]}
                        onCheckedChange={(v) => setQuickSelectVisible(key, v === true)}
                      />
                      <span>{QUICK_SELECT_LABELS[key]}</span>
                    </label>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}
      {/* Always-rendered bulk-action toolbar. Buttons disable when no rows
          are selected so the row count below the toolbar doesn't shift up
          when the user toggles a checkbox — the layout stays stable. */}
      <div className="flex flex-wrap items-center gap-2 border-t bg-muted/40 px-4 py-2 text-sm">
        <span className={cn('font-medium', selectedCount === 0 && 'text-muted-foreground')}>
          {selectedCount > 0 ? `${selectedCount} selected` : 'No rows selected'}
        </span>
        <span className="text-muted-foreground">·</span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={selectedCount === 0}
          onClick={() => setBulkEditOpen(true)}
        >
          Bulk Edit…
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={selectedCount === 0}
          onClick={onBulkRematch}
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Re-match
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={selectedCount === 0}
          onClick={onBulkRevert}
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          Revert
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="text-destructive hover:text-destructive"
          disabled={selectedCount === 0}
          onClick={() => setBulkDeleteOpen(true)}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Delete
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="text-muted-foreground"
          disabled={selectedCount === 0}
          onClick={clearSelection}
        >
          <X className="h-3.5 w-3.5 mr-1" />
          Clear
        </Button>
      </div>
      </div>
      <CardContent className="p-0">
        {lineItems.length === 0 ? (
          <div className="border-t px-6 py-6">
            {readOnly ? (
              <p className="text-sm text-muted-foreground">
                {/* 14ykpqtr22u — every line was in kilograms (a "per kg price" inquiry). */}
                {isKgOnlyDraft(draft) ? KG_ONLY_NOTE : 'This quotation has no line items.'}
              </p>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-sm font-medium">
                    {isKgOnlyDraft(draft) ? KG_ONLY_NOTE : 'No line items yet'}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Paste text or upload one or more files and the AI will extract rebar line items.
                    You can also use <span className="font-medium">+ Add Line</span> below to add one manually.
                  </p>
                </div>
                <IntakeSourceForm
                  submitLabel="Extract items"
                  disabled={isExtracting}
                  onSubmit={(source) => extractIntoDraft(draft.id, source)}
                />
              </>
            )}
          </div>
        ) : (
          <>
            {/* One bounded scroll box owns BOTH axes so the sticky <thead> pins
                within it while rows scroll, and horizontal scroll is preserved
                (86d3fb5yh, mirrors SO #393). z-20 on the thead is deliberate: the
                body's left-pinned cells sit at z-10 (DataTableRow default), so a
                z-10 thead would let them paint over the header on scroll. The
                pinned HEADER cells keep bg-background (opaque) so the pinned column
                doesn't bleed through the frosted header at the top-left corner. */}
            <Table containerClassName="max-h-[60vh]">
              {/* Frosted blur lives on the non-pinned header cells, not the <thead>:
                  a backdrop-filter ancestor suppresses the sticky pinned cells' border
                  + shadow (the matched-SKU divider). Keeping the blur off the pinned
                  cells lets that divider render like the rows (86d3fdmhb). */}
              <TableHeader className="sticky top-0 z-20">
                {table.getHeaderGroups().map((hg) => (
                  <TableRow key={hg.id}>
                    {hg.headers.map((h) => {
                      const meta = h.column.columnDef.meta as
                        | { headerClassName?: string }
                        | undefined;
                      const isPinned = h.column.getIsPinned() === 'left';
                      const isLast = isPinned && h.column.id === LAST_PINNED_ID;
                      const cls = [
                        // Pinned cells stay opaque (no backdrop-filter ancestor → their
                        // sticky divider renders); non-pinned cells carry the frosted blur.
                        isPinned
                          ? 'bg-background'
                          : 'bg-muted/30 backdrop-blur supports-[backdrop-filter]:bg-muted/30',
                        meta?.headerClassName,
                        // Header→body separator is an inset box-shadow, NOT a border-b: a
                        // real border is eaten here both by the collapsed <tr> border and
                        // by the non-pinned cells' own backdrop-blur layer (the line showed
                        // only under the unblurred pinned SKU cell). An inset shadow paints
                        // on the cell's own layer, immune to both, so it renders uniformly.
                        // isLast also keeps the right divider shadow (86d3fdmhb).
                        isLast
                          ? 'border-r shadow-[inset_0_-1px_0_0_hsl(var(--border)),2px_0_4px_-2px_rgba(0,0,0,0.08)]'
                          : 'shadow-[inset_0_-1px_0_0_hsl(var(--border))]',
                      ]
                        .filter(Boolean)
                        .join(' ');
                      return (
                        <TableHead
                          key={h.id}
                          style={{
                            width: h.getSize(),
                            minWidth: h.getSize(),
                            ...pinnedStyle(h.column, 20),
                          }}
                          className={cls || undefined}
                        >
                          {h.isPlaceholder
                            ? null
                            : flexRender(h.column.columnDef.header, h.getContext())}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              {/*
                readOnly: block clicks on row cells so editable cells (spec dropdowns,
                pricing inputs) can't open. The header stays fully interactive so users
                keep sort. Horizontal scroll is driven by the parent container, so it's
                unaffected by pointer-events-none here.
              */}
              <TableBody className={readOnly ? 'pointer-events-none' : undefined}>
                {table.getRowModel().rows.map((row) => (
                  <DataTableRow key={row.id} row={row} lastPinnedLeftId={LAST_PINNED_ID} />
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
      {/* CSC-291 / 86d3fb5yh: single action + filter row above the Weight Breakdown.
          Add Line / Re-extract sit on the left (editable drafts only); the second
          copy of the All/Spec/Pricing view filter is pushed to the right via ml-auto
          (shown whenever there are rows, including read-only) and stays bound to the
          same local viewMode so top and bottom stay in sync. The row renders if
          either side has content. */}
      {(!readOnly || lineItems.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 border-t px-4 py-3">
          {!readOnly && (
            <>
              <Button type="button" variant="outline" size="sm" onClick={handleAddLine}>
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add Line
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setExtractOpen(true)}
                disabled={isExtracting}
                className={isExtracting ? 'border-info/30 bg-info/10 text-info disabled:opacity-100' : undefined}
                title={lineItems.length === 0 ? 'Paste text or upload a file to extract line items' : 'Replace the current line items with a fresh extraction'}
              >
                {isExtracting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin text-info" />
                    Extracting…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                    {lineItems.length === 0 ? 'Extract from source' : 'Re-extract from source'}
                  </>
                )}
              </Button>
            </>
          )}
          {lineItems.length > 0 && (
            <Tabs
              value={viewMode}
              onValueChange={(v) => setViewMode(v as LineItemsViewMode)}
              className="ml-auto"
            >
              <TabsList className="h-8">
                <TabsTrigger value="all" className="px-3 text-xs">All</TabsTrigger>
                <TabsTrigger value="spec" className="px-3 text-xs">Spec</TabsTrigger>
                <TabsTrigger value="pricing" className="px-3 text-xs">Pricing</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>
      )}
      {weightBreakdown.total > 0 && (
        <div className="border-t bg-muted/40 px-4 py-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-foreground text-background shadow-sm">
                <Scale className="h-3.5 w-3.5" />
              </div>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground">
                Weight Breakdown
              </span>
            </div>
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              By diameter · {weightBreakdown.rows.length}{' '}
              {weightBreakdown.rows.length === 1 ? 'size' : 'sizes'}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 mb-3">
            <div className="border border-border bg-card px-3 py-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Total
              </div>
              <div className="mt-0.5 flex items-baseline gap-1.5">
                <span className="text-xl font-bold tabular-nums text-foreground">
                  {formatWeight(weightBreakdown.total)}
                </span>
                <span className="text-[11px] tabular-nums text-muted-foreground">
                  {Math.round(weightBreakdown.total).toLocaleString()} kg
                </span>
              </div>
            </div>
            <div className="border border-info/30 bg-info/10 px-3 py-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-info">
                  Small
                </span>
                <span className="text-[10px] font-medium tabular-nums text-info/80">
                  {weightBreakdown.smallCaption}
                </span>
              </div>
              <div className="mt-0.5 flex items-baseline gap-1.5">
                <span className="text-xl font-bold tabular-nums text-foreground">
                  {formatSmallPct(weightBreakdown.smallPct)}
                </span>
                <span className="text-[11px] tabular-nums text-info/80">
                  {formatWeight(weightBreakdown.smallWeight)}
                </span>
              </div>
            </div>
            <div className="border border-border bg-muted/60 px-3 py-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground">
                  Big
                </span>
                <span className="text-[10px] font-medium tabular-nums text-muted-foreground">
                  {weightBreakdown.bigCaption}
                </span>
              </div>
              <div className="mt-0.5 flex items-baseline gap-1.5">
                <span className="text-xl font-bold tabular-nums text-foreground">
                  {formatSmallPct(weightBreakdown.bigPct)}
                </span>
                <span className="text-[11px] tabular-nums text-muted-foreground">
                  {formatWeight(weightBreakdown.bigWeight)}
                </span>
              </div>
            </div>
          </div>

          <div
            className="flex h-3 w-full overflow-hidden rounded-full bg-muted ring-1 ring-border"
            role="img"
            aria-label={`Weight composition by diameter: ${weightBreakdown.rows
              .map((r) => `${r.diameter}mm ${formatSmallPct(r.pct)}`)
              .join(', ')}`}
          >
            {weightBreakdown.rows.map((row) => (
              <div
                key={row.diameter}
                className={colorsForDiameter(row.diameter).bar}
                style={{ width: `${row.pct * 100}%` }}
                title={`${row.diameter}mm · ${formatWeight(row.weight)} · ${formatSmallPct(row.pct)}`}
              />
            ))}
            {weightBreakdown.unspecified > 0 && (
              <div
                className="bg-warning"
                style={{ width: `${(weightBreakdown.unspecified / weightBreakdown.total) * 100}%` }}
                title={`No size · ${formatWeight(weightBreakdown.unspecified)} · ${formatSmallPct(weightBreakdown.unspecified / weightBreakdown.total)}`}
              />
            )}
          </div>

          <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
            {weightBreakdown.rows.map((row) => {
              const colors = colorsForDiameter(row.diameter);
              return (
                <div key={row.diameter} className="flex items-center gap-1.5 tabular-nums">
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-sm ${colors.swatch}`} />
                  <span className={`font-semibold ${colors.text}`}>{row.diameter}mm</span>
                  <span className="font-semibold text-foreground">{formatSmallPct(row.pct)}</span>
                  <span className="text-muted-foreground">({formatWeight(row.weight)})</span>
                </div>
              );
            })}
            {weightBreakdown.unspecified > 0 && (
              <div className="flex items-center gap-1.5 tabular-nums">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-warning" />
                <span className="font-semibold text-warning">No size</span>
                <span className="font-semibold text-foreground">
                  {formatSmallPct(weightBreakdown.unspecified / weightBreakdown.total)}
                </span>
                <span className="text-muted-foreground">({formatWeight(weightBreakdown.unspecified)})</span>
              </div>
            )}
          </div>
        </div>
      )}
      <BulkEditDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        selectedCount={selectedCount}
        onApply={onBulkApply}
      />
      <BulkDeleteConfirm
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        count={selectedCount}
        onConfirm={onBulkDelete}
      />
      <ExtractFromSourceDialog
        open={extractOpen}
        onOpenChange={setExtractOpen}
        draft={draft}
      />
    </Card>
  );
}

// CSC-293: right-click menu on a spec cell. Three actions — select-only,
// add-to-selection, remove-from-selection — scoped to ids of rows sharing the
// cell's value. When `readOnly` is true the wrapper passes children through
// so right-click does not open a menu (Submitted / Sent quotations stay
// non-mutating).
function SpecContextMenu({
  readOnly,
  matchIds,
  label,
  onSelectOnly,
  onAdd,
  onRemove,
  children,
}: {
  readOnly: boolean;
  matchIds: string[];
  label: string;
  onSelectOnly: (ids: string[]) => void;
  onAdd: (ids: string[]) => void;
  onRemove: (ids: string[]) => void;
  children: React.ReactNode;
}) {
  if (readOnly) return <>{children}</>;
  const count = matchIds.length;
  const disabled = count === 0;
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className="w-full">{children}</div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        <ContextMenuItem disabled={disabled} onSelect={() => onSelectOnly(matchIds)}>
          Select {label} only{count > 0 ? ` · ${count}` : ''}
        </ContextMenuItem>
        <ContextMenuItem disabled={disabled} onSelect={() => onAdd(matchIds)}>
          Add {label} to selection
        </ContextMenuItem>
        <ContextMenuItem disabled={disabled} onSelect={() => onRemove(matchIds)}>
          Remove {label} from selection
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

